import { useMemo, useRef, useState } from "react";
import { BankCard, BankShell, bankInputClass } from "../components/bank/Shell.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";
import { FILTER_INPUT } from "../components/common/FilterBar.js";
import { formatMoney } from "../lib/format-fr.js";

const STATUS_TONE: Record<string, string> = {
  pending: "bg-neutral-700/40 text-neutral-300",
  processing: "bg-sky-500/15 text-sky-300",
  completed: "bg-lime/15 text-lime",
  failed: "bg-red-500/15 text-red-300",
};

export default function BankImport() {
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const t = useT();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const [bankAccountId, setBankAccountId] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<string | null>(null);
  const modals = useModals();
  // F-conta-errada — o último arquivo, para reenviar com "importar mesmo assim".
  const ultimoEnvio = useRef<{
    companyId: number;
    bankAccountId: number;
    filename: string;
    /** CSV (texto) ou relevé PDF da SG (base64) — CEO 2026-11-03. */
    csvContent?: string;
    pdfBase64?: string;
  } | null>(null);
  // F-desfazer-import (CEO 2026-11-02)
  const [desfazerId, setDesfazerId] = useState<number | null>(null);
  // F-conta-certa (CEO 2026-11-02) — o arquivo escolhido é conferido antes de
  // gravar; só "Importar na conta X" grava.
  const previewMutation = trpc.bankImport.previewCSV.useMutation({
    onError: (e) => {
      setUploadError(e.message);
      setUploadResult(null);
    },
  });
  const previa = previewMutation.data ?? null;
  const limparPrevia = () => {
    previewMutation.reset();
    ultimoEnvio.current = null;
  };
  const [mostrarVazios, setMostrarVazios] = useState(false);
  const isAdmin =
    !!user?.isSuperAdmin ||
    (user?.memberships ?? []).some(
      (m) => m.companyId === companyId && m.active && m.role === "admin",
    );

  const accountsQuery = trpc.bankAccounts.list.useQuery(
    { companyId: companyId ?? 0, limit: 50, offset: 0 },
    { enabled: companyId !== null },
  );
  const batchesQuery = trpc.bankImport.listBatches.useQuery(
    {
      companyId: companyId ?? 0,
      bankAccountId: bankAccountId ?? undefined,
      limit: 50,
      offset: 0,
      hideEmptyFailed: !mostrarVazios,
    },
    { enabled: companyId !== null },
  );

  const utils = trpc.useUtils();
  const uploadMutation = trpc.bankImport.uploadCSV.useMutation({
    onSuccess: (res) => {
      limparPrevia();
      setUploadResult(
        `${t("bankImport.importOk")} — ${res?.rowsImported ?? 0} ${t("bankImport.importedWord")} · ${res?.rowsSkipped ?? 0} ${t("bankImport.duplicatedWord")} · ${res?.rowsFailed ?? 0} ${t("bankImport.failedWord")} (${res?.rowsTotal ?? 0} ${t("bankImport.totalWord")}).`,
      );
      setUploadError(null);
      utils.bankImport.invalidate();
      utils.bankTransactions.invalidate();
    },
    onError: (e) => {
      // F-conta-errada (CEO 2026-11-02) — os movimentos já estão noutra conta:
      // confirma antes de gravar (o mais provável é a conta escolhida errada).
      if (e.data?.code === "CONFLICT" && ultimoEnvio.current) {
        const envio = ultimoEnvio.current;
        setUploadError(e.message);
        setUploadResult(null);
        void modals
          .confirm({
            title: t("bankImport.wrongAccountTitle"),
            message: `${e.message} ${t("bankImport.wrongAccountHint")}`,
            confirmLabel: t("bankImport.importAnywayBtn"),
            tone: "danger",
          })
          .then((ok) => {
            if (ok) {
              setUploadError(null);
              uploadMutation.mutate({ ...envio, ignorarOutraConta: true });
            }
          });
        return;
      }
      setUploadError(e.message);
      setUploadResult(null);
    },
  });
  const syncWiseMutation = trpc.bankImport.syncWise.useMutation({
    onSuccess: (res) => {
      setUploadResult(
        `${t("bankImport.syncWiseOk")} — ${res?.inserted ?? 0} ${t("bankImport.importedWord")} · ${res?.skipped ?? 0} ${t("bankImport.duplicatedWord")} · 0 ${t("bankImport.failedWord")}.`,
      );
      setUploadError(null);
      utils.bankImport.invalidate();
      utils.bankTransactions.invalidate();
    },
    onError: (e) => {
      setUploadError(e.message);
      setUploadResult(null);
    },
  });

  const accounts = useMemo(() => accountsQuery.data?.rows ?? [], [accountsQuery.data?.rows]);
  const selectedAccount = accounts.find((a) => a.id === bankAccountId) ?? null;
  const accountById = new Map(accounts.map((a) => [a.id, a]));
  const statusLabels: Record<string, string> = {
    pending: t("bankImport.statusPending"),
    processing: t("bankImport.statusProcessing"),
    completed: t("bankImport.statusCompleted"),
    failed: t("bankImport.statusFailed"),
  };

  // F-conta-certa (CEO 2026-11-02) — a tela NÃO escolhe a conta sozinha: na BS
  // a 1ª em ordem alfabética virou "Wamo" e o extrato do Wise foi parar lá.

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    setUploadError(null);
    setUploadResult(null);
    const file = e.target.files?.[0];
    if (!file || !companyId || !bankAccountId) return;
    // CEO 2026-11-03 — relevé da SG em PDF: vai em base64 (o servidor lê).
    if (/\.pdf$/i.test(file.name) || file.type === "application/pdf") {
      const leitor = new FileReader();
      leitor.onload = () => {
        const url = String(leitor.result ?? "");
        const pdfBase64 = url.slice(url.indexOf(",") + 1);
        if (!pdfBase64) {
          setUploadError(t("bankImport.emptyFile"));
          return;
        }
        ultimoEnvio.current = { companyId, bankAccountId, filename: file.name, pdfBase64 };
        previewMutation.mutate({ companyId, bankAccountId, filename: file.name, pdfBase64 });
      };
      leitor.onerror = () => setUploadError(t("bankImport.fileReadError"));
      leitor.readAsDataURL(file);
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? "");
      if (!text) {
        setUploadError(t("bankImport.emptyFile"));
        return;
      }
      ultimoEnvio.current = { companyId, bankAccountId, filename: file.name, csvContent: text };
      previewMutation.mutate({
        companyId,
        bankAccountId,
        filename: file.name,
        csvContent: text,
      });
    };
    reader.onerror = () => setUploadError(t("bankImport.fileReadError"));
    reader.readAsText(file, "utf-8");
    // Reset do input para permitir re-upload do mesmo arquivo
    e.target.value = "";
  }

  if (!user) return null;
  if (accessibleCompanies.length === 0) {
    return (
      <BankShell subtitle={t("bankImport.importSubtitle")}>
        <p className="text-sm text-neutral-400">{t("bankImport.noCompanyAccess")}</p>
      </BankShell>
    );
  }

  const batches = batchesQuery.data ?? [];

  const toolbar = (
    <div className="flex items-center gap-3 flex-wrap">
      <select
        value={companyId ?? ""}
        onChange={(e) => {
          setCompanyId(Number(e.target.value));
          setBankAccountId(null);
          limparPrevia();
        }}
        className={FILTER_INPUT + " w-60"}
      >
        {accessibleCompanies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <select
        value={bankAccountId ?? ""}
        onChange={(e) => {
          setBankAccountId(e.target.value === "" ? null : Number(e.target.value));
          limparPrevia();
          setUploadError(null);
          setUploadResult(null);
        }}
        className={
          FILTER_INPUT + " w-64" + (bankAccountId == null ? " border-[var(--warning-500)]" : "")
        }
        data-testid="import-account"
      >
        <option value="">{t("bankImport.selectAccount")}</option>
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>
            {a.label} · {a.provider}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <BankShell subtitle={t("bankImport.importStatements")} toolbar={toolbar}>
      {!canEdit && (
        <BankCard title={t("bankImport.importStatements")}>
          <p className="text-sm text-neutral-400">{t("bankImport.readOnlyNotice")}</p>
        </BankCard>
      )}
      {canEdit && (
        <>
          <BankCard
            title={
              selectedAccount?.provider === "societe_generale"
                ? t("bankImport.importCsvOrPdf")
                : t("bankImport.importCsv")
            }
            subtitle={
              selectedAccount
                ? `${t("bankImport.accountProvider")}: ${selectedAccount.provider}`
                : t("bankImport.selectAccountAbove")
            }
          >
            <div className="space-y-3">
              <p className="text-sm text-neutral-400">
                {selectedAccount?.provider === "societe_generale"
                  ? t("bankImport.sgUploadHint")
                  : t("bankImport.genericUploadHint")}{" "}
                {t("bankImport.idempotentNote")}
              </p>
              {!bankAccountId && (
                <p
                  className="rounded-md border border-[var(--warning-500)] bg-[var(--warning-50)] px-3 py-2 text-sm text-[var(--text-strong)]"
                  data-testid="import-choose-account"
                >
                  {t("bankImport.chooseAccountFirst")}
                </p>
              )}
              <input
                type="file"
                accept={
                  selectedAccount?.provider === "societe_generale"
                    ? ".csv,.pdf,text/csv,application/pdf"
                    : ".csv,text/csv"
                }
                onChange={handleFileChange}
                disabled={uploadMutation.isPending || previewMutation.isPending || !bankAccountId}
                data-testid="import-file"
                className="block text-sm text-neutral-300 file:mr-3 file:rounded-md file:border file:border-lime/40 file:bg-lime/10 file:hover:bg-lime/20 file:px-4 file:py-1.5 file:text-lime file:cursor-pointer disabled:opacity-50"
              />
              {(uploadMutation.isPending || previewMutation.isPending) && (
                <p className="text-sm text-sky-300">{t("bankImport.processing")}</p>
              )}
              {previa && !uploadMutation.isPending && (
                <PreviaImport
                  previa={previa}
                  filename={ultimoEnvio.current?.filename ?? ""}
                  onCancel={limparPrevia}
                  onConfirm={() => {
                    const envio = ultimoEnvio.current;
                    if (!envio) return;
                    uploadMutation.mutate({
                      ...envio,
                      ...(previa.outraConta?.trava ? { ignorarOutraConta: true } : {}),
                    });
                  }}
                />
              )}
              {uploadResult && (
                <div className="text-sm text-lime bg-lime/10 border border-lime/30 rounded-md px-3 py-2">
                  {uploadResult}
                </div>
              )}
              {uploadError && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
                  {uploadError}
                </div>
              )}
            </div>
          </BankCard>

          <BankCard
            title={t("bankImport.wiseSyncTitle")}
            subtitle={
              selectedAccount?.provider === "wise"
                ? `${t("bankImport.accountConfiguredPre")} '${selectedAccount.label}' ${t("bankImport.accountConfiguredSuf")}`
                : t("bankImport.wiseOnlyHint")
            }
          >
            <button
              onClick={() => {
                if (!companyId || !bankAccountId) return;
                syncWiseMutation.mutate({ companyId, bankAccountId });
              }}
              disabled={
                syncWiseMutation.isPending || !bankAccountId || selectedAccount?.provider !== "wise"
              }
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
            >
              {syncWiseMutation.isPending
                ? t("bankImport.syncing")
                : `↻ ${t("bankImport.syncWiseBtn")}`}
            </button>
          </BankCard>
        </>
      )}

      <BankCard
        title={`${t("bankImport.importHistory")} (${batches.length})`}
        padded={false}
        action={
          <label className="flex items-center gap-2 text-xs text-neutral-400">
            <input
              type="checkbox"
              checked={mostrarVazios}
              onChange={(e) => setMostrarVazios(e.target.checked)}
              className="accent-lime"
              data-testid="batches-show-empty"
            />
            {t("bankImport.showEmptyFailed")}
          </label>
        }
      >
        {batches.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("bankImport.emptyBatches")}</p>
        )}
        {batches.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left px-3 py-2 w-32">{t("bankImport.colDate")}</th>
                  <th className="text-left px-3 py-2 w-32">{t("bankImport.colAccount")}</th>
                  <th className="text-left px-3 py-2 w-24">{t("bankImport.colMethod")}</th>
                  <th className="text-left px-3 py-2">{t("bankImport.colFile")}</th>
                  <th className="text-right px-3 py-2 w-16">{t("bankImport.colTotal")}</th>
                  <th className="text-right px-3 py-2 w-16">{t("bankImport.colImported")}</th>
                  <th className="text-right px-3 py-2 w-16">{t("bankImport.colDuplicated")}</th>
                  <th className="text-right px-3 py-2 w-16">{t("bankImport.colFailed")}</th>
                  <th className="text-left px-3 py-2 w-24">{t("bankImport.colStatus")}</th>
                  {isAdmin && <th className="px-3 py-2 w-28" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {batches.map((b) => {
                  const acc = accountById.get(b.bankAccountId);
                  return (
                    <tr
                      key={b.id}
                      className={b.undone ? "opacity-60" : ""}
                      data-testid={`batch-row-${b.id}`}
                    >
                      <td className="px-3 py-2 text-xs text-neutral-400">
                        {new Date(b.createdAt).toLocaleString("fr-FR")}
                      </td>
                      <td className="px-3 py-2 text-xs">{acc?.label ?? `#${b.bankAccountId}`}</td>
                      <td className="px-3 py-2 text-xs text-neutral-400">
                        {(b.metadata as { format?: string } | null)?.format === "pdf"
                          ? "pdf_upload"
                          : b.importMethod}
                      </td>
                      <td className="px-3 py-2 text-xs text-neutral-400 font-mono truncate max-w-xs">
                        {b.filename ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-xs font-mono">{b.rowsTotal}</td>
                      <td className="px-3 py-2 text-right text-xs font-mono text-lime">
                        {b.rowsImported}
                      </td>
                      <td className="px-3 py-2 text-right text-xs font-mono text-neutral-400">
                        {b.rowsSkipped}
                      </td>
                      <td className="px-3 py-2 text-right text-xs font-mono text-red-400">
                        {b.rowsFailed}
                      </td>
                      <td className="px-3 py-2">
                        {b.undone ? (
                          <span
                            className="inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs bg-neutral-700/40 text-neutral-300"
                            title={`${new Date(b.undone.at).toLocaleString("fr-FR")} — ${b.undone.reason}`}
                            data-testid={`batch-undone-${b.id}`}
                          >
                            ↶ {t("bankImport.statusUndone")} ({b.undone.deletedRows})
                          </span>
                        ) : (
                          <span
                            className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_TONE[b.status]}`}
                          >
                            {statusLabels[b.status] ?? b.status}
                          </span>
                        )}
                      </td>
                      {isAdmin && (
                        <td className="px-3 py-2 text-right">
                          {!b.undone && b.status === "completed" && b.rowsImported > 0 && (
                            <button
                              type="button"
                              onClick={() => setDesfazerId(b.id)}
                              className="whitespace-nowrap rounded-md border border-red-500/40 px-2 py-1 text-xs text-red-400 hover:border-red-500"
                              data-testid={`batch-undo-${b.id}`}
                            >
                              ↶ {t("bankImport.undoBtn")}
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </BankCard>
      {desfazerId != null && companyId != null && (
        <DesfazerImport
          companyId={companyId}
          batchId={desfazerId}
          contaLabel={(id) => accountById.get(id)?.label ?? `#${id}`}
          onClose={() => setDesfazerId(null)}
        />
      )}
    </BankShell>
  );
}

type Previa = {
  conta: { id: number; label: string; provider: string };
  empresa: string;
  bancoLabel: string | null;
  bloqueio: string | null;
  movimentos: number;
  falhas: number;
  de: string | null;
  ate: string | null;
  saldoFinal: { amount: number; date: string } | null;
  jaNestaConta: number;
  outraConta: { label: string; iguais: number; total: number; trava: boolean } | null;
};

/**
 * F-conta-certa (CEO 2026-11-02) — "Confira antes de importar": banco do
 * arquivo, movimentos, período, saldo final e, em destaque, a conta de
 * destino. Arquivo de outro banco: só Cancelar. Movimentos já em outra conta:
 * aviso e "Importar mesmo assim na conta X".
 */
function PreviaImport({
  previa,
  filename,
  onCancel,
  onConfirm,
}: {
  previa: Previa;
  filename: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const t = useT();
  const fmtData = (d: string | null) => (d ? d.split("-").reverse().join("/") : "—");
  const outra = previa.outraConta;
  const travaOutra = !!outra?.trava;
  return (
    <div
      className="space-y-3 rounded-lg border border-[var(--border-strong)] bg-[var(--surface-card)] p-4"
      data-testid="import-preview"
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
        {t("bankImport.previewTitle")}
      </p>
      <div
        className={
          previa.bloqueio
            ? "rounded-md border-2 border-[var(--danger-500)] bg-red-500/5 px-3 py-2"
            : "rounded-md border-2 border-lime/60 bg-lime/10 px-3 py-2"
        }
        data-testid="import-preview-dest"
      >
        <div className="text-xs text-[var(--text-muted)]">{t("bankImport.previewGoesTo")}</div>
        <div className="text-lg font-bold text-[var(--text-strong)]">{previa.conta.label}</div>
        <div className="text-xs text-[var(--text-muted)]">{previa.empresa}</div>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-[var(--text-muted)]">{t("bankImport.previewFile")}</dt>
        <dd className="break-all font-mono text-xs text-[var(--text-body)]">{filename}</dd>
        <dt className="text-[var(--text-muted)]">{t("bankImport.previewBank")}</dt>
        <dd className="text-[var(--text-body)]" data-testid="import-preview-bank">
          {previa.bancoLabel ?? t("bankImport.previewBankUnknown")}
        </dd>
        {!previa.bloqueio && (
          <>
            <dt className="text-[var(--text-muted)]">{t("bankImport.previewMovements")}</dt>
            <dd className="text-[var(--text-body)]" data-testid="import-preview-count">
              <strong>{previa.movimentos}</strong> · {fmtData(previa.de)} → {fmtData(previa.ate)}
              {previa.falhas > 0 && (
                <span className="text-[var(--danger-500)]">
                  {" "}
                  · {previa.falhas} {t("bankImport.failedWord")}
                </span>
              )}
            </dd>
            {previa.saldoFinal && (
              <>
                <dt className="text-[var(--text-muted)]">{t("bankImport.previewBalance")}</dt>
                <dd className="text-[var(--text-body)]">
                  {formatMoney(previa.saldoFinal.amount, { decimals: 2 })} ·{" "}
                  {fmtData(previa.saldoFinal.date)}
                </dd>
              </>
            )}
            {previa.jaNestaConta > 0 && (
              <>
                <dt className="text-[var(--text-muted)]">{t("bankImport.previewAlready")}</dt>
                <dd className="text-[var(--text-body)]">
                  {previa.jaNestaConta} {t("bankImport.previewAlreadySuf")}
                </dd>
              </>
            )}
          </>
        )}
      </dl>
      {previa.bloqueio && (
        <p
          className="rounded-md border border-[var(--danger-500)] bg-red-500/10 px-3 py-2 text-sm font-medium text-[var(--danger-500)]"
          data-testid="import-preview-blocked"
        >
          ⛔ {previa.bloqueio}
        </p>
      )}
      {!previa.bloqueio && outra && outra.iguais > 0 && (
        <p
          className="rounded-md border border-[var(--warning-500)] bg-[var(--warning-50)] px-3 py-2 text-sm text-[var(--text-strong)]"
          data-testid="import-preview-other"
        >
          ⚠ {outra.iguais} {t("bankImport.previewOtherMid")} {outra.total}{" "}
          {t("bankImport.previewOtherSuf")} <strong>{outra.label}</strong>.
          {travaOutra && <> {t("bankImport.previewOtherHint")}</>}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-[var(--border-strong)] px-3 py-1.5 text-sm text-[var(--text-body)]"
          data-testid="import-preview-cancel"
        >
          {t("bankImport.previewCancel")}
        </button>
        {!previa.bloqueio && previa.movimentos > 0 && (
          <button
            type="button"
            onClick={onConfirm}
            className={
              travaOutra
                ? "rounded-md bg-[var(--danger-500)] px-4 py-1.5 text-sm font-medium text-white"
                : "rounded-md bg-lime px-4 py-1.5 text-sm font-medium text-on-lime"
            }
            data-testid="import-preview-confirm"
          >
            {travaOutra ? t("bankImport.previewImportAnyway") : t("bankImport.previewImportTo")}{" "}
            {previa.conta.label}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * F-desfazer-import (CEO 2026-11-02) — confirma o que sai (N movimentos, período,
 * conta) e pede o motivo. Se algum movimento já foi usado, mostra o que trava e
 * não deixa desfazer.
 */
function DesfazerImport({
  companyId,
  batchId,
  contaLabel,
  onClose,
}: {
  companyId: number;
  batchId: number;
  contaLabel: (id: number) => string;
  onClose: () => void;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const [motivo, setMotivo] = useState("");
  // Confere sempre na hora de abrir: um vínculo desfeito agora precisa valer já.
  const check = trpc.bankImport.undoCheck.useQuery(
    { companyId, batchId },
    { refetchOnMount: "always", gcTime: 0 },
  );
  const desfazer = trpc.bankImport.undoBatch.useMutation({
    onSuccess: () => {
      void utils.bankImport.invalidate();
      void utils.bankTransactions.invalidate();
      void utils.bankAccounts.invalidate();
      onClose();
    },
  });
  const c = check.isFetching ? undefined : check.data;
  const fmtData = (d: string | null) => (d ? d.split("-").reverse().join("/") : "—");
  const travas = c
    ? [
        [c.conciliados, t("bankImport.undoBlockReconciled")],
        [c.vinculados, t("bankImport.undoBlockLinked")],
        [c.comAnexo, t("bankImport.undoBlockAttachment")],
        [c.emExercicioFechado, t("bankImport.undoBlockClosedYear")],
        [c.distribuidosNaTesouraria, t("bankImport.undoBlockTreasury")],
      ].filter(([n]) => Number(n) > 0)
    : [];
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md space-y-3 rounded-xl border border-[var(--border-strong)] bg-[var(--surface-card)] p-5 shadow-[var(--shadow-sm)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="batch-undo-modal"
      >
        <h2 className="text-lg font-bold text-[var(--text-strong)]">{t("bankImport.undoTitle")}</h2>
        {check.isFetching && <p className="text-sm text-[var(--text-muted)]">…</p>}
        {check.error && <p className="text-sm text-[var(--danger-500)]">{check.error.message}</p>}
        {c && (
          <>
            <div className="rounded-md border border-[var(--border)] bg-[var(--surface-muted)] px-3 py-2 text-sm text-[var(--text-body)]">
              <div className="break-all font-mono text-xs">{c.filename ?? "—"}</div>
              <div className="mt-1">
                {t("bankImport.colAccount")}: <strong>{contaLabel(c.bankAccountId)}</strong>
              </div>
              <div data-testid="batch-undo-summary">
                <strong>{c.movimentos}</strong> {t("bankImport.undoMovements")} · {fmtData(c.de)} →{" "}
                {fmtData(c.ate)}
              </div>
            </div>
            {c.podeDesfazer ? (
              <>
                <p className="text-sm text-[var(--text-body)]">{t("bankImport.undoExplain")}</p>
                <label className="block text-xs text-[var(--text-muted)]">
                  {t("bankImport.undoReasonLabel")}
                  <textarea
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    rows={2}
                    placeholder={t("bankImport.undoReasonPlaceholder")}
                    className="mt-1 w-full rounded-md border border-[var(--border)] bg-[var(--surface-page)] px-3 py-1.5 text-sm text-[var(--text-body)] outline-none focus:border-lime"
                    data-testid="batch-undo-reason"
                  />
                </label>
              </>
            ) : (
              <div
                className="rounded-md border border-[var(--warning-500)] bg-[var(--warning-50)] px-3 py-2 text-sm text-[var(--text-strong)]"
                data-testid="batch-undo-blocked"
              >
                <p className="font-medium">
                  {c.jaDesfeito ? t("bankImport.undoAlready") : t("bankImport.undoBlockedTitle")}
                </p>
                {travas.length > 0 && (
                  <ul className="mt-1 list-disc pl-5">
                    {travas.map(([n, label]) => (
                      <li key={String(label)}>
                        {n} {label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {desfazer.error && (
              <p className="text-sm text-[var(--danger-500)]">{desfazer.error.message}</p>
            )}
          </>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[var(--border-strong)] px-3 py-1.5 text-sm text-[var(--text-body)]"
          >
            {t("bankImport.closeBtn")}
          </button>
          {c?.podeDesfazer && (
            <button
              type="button"
              disabled={desfazer.isPending || motivo.trim().length < 3}
              onClick={() => desfazer.mutate({ companyId, batchId, reason: motivo.trim() })}
              className="rounded-md bg-[var(--danger-500)] px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              data-testid="batch-undo-confirm"
            >
              ↶ {t("bankImport.undoConfirmBtn")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
